/**
 * RisksIssuesTab — Management sub-tab for the Flag → Risk → Issue
 * three-tier governance model.
 *
 * Three views:
 *   • Flag Inbox  — live data from /api/projects/{guid}/issue-flags
 *   • Risk Register — full CRUD from /api/projects/{guid}/risks
 *   • Issues Register — full CRUD from /api/projects/{guid}/issues
 *
 * Each register has:
 *   - Horizontal filter bar (status, category, role, search)
 *   - Tabular list of items
 *   - Overlay detail drawer with read + edit modes
 *   - L×I assessment panel (risks only) with piecewise color gradient
 *
 * Epic: DEV-683
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchIssueFlags,
  resolveIssueFlag,
  promoteIssueFlag,
  type IssueFlagResponse,
  type IssueSeverity,
  type IssueStatus,
} from '../../api/issueFlags';
import {
  fetchProjectRisks,
  createProjectRisk,
  updateProjectRisk,
  deleteProjectRisk,
  materializeRisk,
  type ProjectRiskResponse,
} from '../../api/projectRisks';
import {
  fetchProjectIssues,
  createProjectIssue,
  updateProjectIssue,
  deleteProjectIssue,
  type ProjectIssueResponse,
} from '../../api/projectIssues';
import {
  fetchAllSecurityUsers,
  type SecurityUserResponse,
} from '../../api/security';
import {
  fetchGanttTasksByProject,
  type GanttTaskResponse,
} from '../../api/ganttTasks';
import {
  fetchProjectRoleSlots,
  type ProjectRoleSlotResponse,
} from '../../api/projectRoles';
import { riskScoreColor, riskScoreBgColor, riskScoreHeatmapBg } from './riskScoreColor';
import {
  RiskDrawer,
  IssueDrawer,
  AssessmentPanel,
  RiskScoreBar,
  formatDate,
  RISK_STATUSES,
  RISK_CATEGORIES,
  ISSUE_STATUSES,
  ISSUE_SEVERITIES,
  ISSUE_CATEGORIES,
} from './RiskIssueDrawers';
// DEV-832: HSE incident entry path from the Issues tab. Reuses the same
// modal the per-card "+ Report" button uses, with category unlocked so
// the user picks Health/Safety/Environment in the modal.
import { ReportIncidentModal } from '../ReportIncidentModal/ReportIncidentModal';
import styles from './RisksIssuesTab.module.css';

type ViewKey = 'flags' | 'risks' | 'issues';
type FlagFilterKey = 'open' | 'resolved' | 'dismissed' | 'all';

export interface RisksIssuesTabProps {
  projectGuid: string;
  canEdit?: boolean;
  debugMode?: boolean;
  onFlagsChanged?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function severityClass(sev: IssueSeverity): string {
  switch (sev) {
    case 'blocking':      return styles.severityBlocking;
    case 'warning':       return styles.severityWarning;
    case 'informational': return styles.severityInfo;
  }
}

function statusClass(status: IssueStatus): string {
  switch (status) {
    case 'open':      return styles.statusOpen;
    case 'resolved':  return styles.statusResolved;
    case 'dismissed': return styles.statusDismissed;
  }
}

function rowClass(flag: IssueFlagResponse): string {
  const sev = flag.severity;
  const sevClass =
    sev === 'blocking' ? styles.flagRowBlocking :
    sev === 'warning'  ? styles.flagRowWarning  :
                         styles.flagRowInfo;
  const statusMod =
    flag.status === 'resolved'  ? styles.flagRowResolved  :
    flag.status === 'dismissed' ? styles.flagRowDismissed :
                                  '';
  return `${styles.flagRow} ${sevClass} ${statusMod}`.trim();
}

/* formatDate imported from RiskIssueDrawers */

function guidKey(g: string | null | undefined): string {
  return (g ?? '').toUpperCase();
}

/* LI_TINTS, LI_COLORS, RiskScoreBar imported from RiskIssueDrawers */

/* ------------------------------------------------------------------ */
/*  Risk Heatmap — 5×5 Likelihood × Impact matrix                     */
/* ------------------------------------------------------------------ */

/** Descriptive labels for likelihood (y-axis) and impact (x-axis). */
const LIKELIHOOD_LABELS: Record<number, string> = {
  1: 'likelihood_1',
  2: 'likelihood_2',
  3: 'likelihood_3',
  4: 'likelihood_4',
  5: 'likelihood_5',
};

const IMPACT_LABELS: Record<number, string> = {
  1: 'impact_1',
  2: 'impact_2',
  3: 'impact_3',
  4: 'impact_4',
  5: 'impact_5',
};

function RiskHeatmap({
  risks,
  onSelectRisk,
  t,
}: {
  risks: ProjectRiskResponse[];
  onSelectRisk: (risk: ProjectRiskResponse) => void;
  t: (key: string) => string;
}) {
  // Assign stable R-N reference numbers by creation order
  const riskRefs = useMemo(() => {
    const sorted = [...risks].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const m = new Map<string, number>();
    sorted.forEach((r, i) => m.set(r.id, i + 1));
    return m;
  }, [risks]);

  // Build a map of (L,I) → risks[]
  const cellMap = useMemo(() => {
    const m = new Map<string, ProjectRiskResponse[]>();
    for (const r of risks) {
      const key = `${r.likelihood}-${r.impact}`;
      const arr = m.get(key) || [];
      arr.push(r);
      m.set(key, arr);
    }
    return m;
  }, [risks]);

  return (
    <div className={styles.heatmapWrapper}>
      <div className={styles.heatmapContainer}>
        {/* Y-axis label */}
        <div className={styles.heatmapYLabel}>
          {t('projectDetail.risksIssues.likelihood')}
        </div>

        <div className={styles.heatmapGrid}>
          {/* Row headers + cells: likelihood 5 at top → 1 at bottom */}
          {[5, 4, 3, 2, 1].map((likelihood) => (
            <React.Fragment key={`row-${likelihood}`}>
              <div className={styles.heatmapRowHeader}>
                <span className={styles.heatmapRowLabel}>
                  {t(`projectDetail.risksIssues.${LIKELIHOOD_LABELS[likelihood]}`)}
                </span>
              </div>
              {[1, 2, 3, 4, 5].map((impact) => {
                const score = likelihood * impact;
                const cellRisks = cellMap.get(`${likelihood}-${impact}`) || [];
                return (
                  <div
                    key={`${likelihood}-${impact}`}
                    className={styles.heatmapCell}
                    style={{ backgroundColor: riskScoreHeatmapBg(score) }}
                  >
                    {cellRisks.map((r) => {
                      const refNum = riskRefs.get(r.id) ?? 0;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          className={styles.heatmapChip}
                          style={{
                            backgroundColor: riskScoreColor(score),
                            borderColor: riskScoreColor(score),
                          }}
                          title={r.title}
                          onClick={() => onSelectRisk(r)}
                        >
                          R-{refNum}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}

          {/* Bottom row: empty corner + impact column headers */}
          <div className={styles.heatmapCorner} />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={`ih-${i}`} className={styles.heatmapColHeader}>
              {t(`projectDetail.risksIssues.${IMPACT_LABELS[i]}`)}
            </div>
          ))}
        </div>
      </div>

      {/* X-axis title */}
      <div className={styles.heatmapXLabel}>
        {t('projectDetail.risksIssues.impact')}
      </div>

      {/* Legend: R-N → risk title */}
      {risks.length > 0 && (
        <div className={styles.heatmapLegend}>
          {[...riskRefs.entries()]
            .sort((a, b) => a[1] - b[1])
            .map(([id, num]) => {
              const risk = risks.find((r) => r.id === id);
              if (!risk) return null;
              const score = risk.likelihood * risk.impact;
              return (
                <button
                  key={id}
                  type="button"
                  className={styles.heatmapLegendItem}
                  onClick={() => onSelectRisk(risk)}
                >
                  <span
                    className={styles.heatmapLegendChip}
                    style={{
                      backgroundColor: riskScoreColor(score),
                      borderColor: riskScoreColor(score),
                    }}
                  >
                    R-{num}
                  </span>
                  <span className={styles.heatmapLegendTitle}>
                    {risk.title}
                  </span>
                  <RiskScoreBar score={score} compact />
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

/* RISK_CATEGORIES, ISSUE_CATEGORIES, RISK_STATUSES, ISSUE_STATUSES,
   ISSUE_SEVERITIES, AssessmentPanel imported from RiskIssueDrawers */

/* RiskDrawer imported from RiskIssueDrawers */
/* IssueDrawer imported from RiskIssueDrawers */

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function RisksIssuesTab({
  projectGuid,
  canEdit = true,
  debugMode,
  onFlagsChanged,
}: RisksIssuesTabProps) {
  const { t } = useAppTranslation('pages');

  /* ── View state ─────────────────────────────────────────────── */
  const [view, setView] = useState<ViewKey>('flags');
  const [flagFilter, setFlagFilter] = useState<FlagFilterKey>('open');
  const [riskView, setRiskView] = useState<'list' | 'heatmap'>('list');
  const [riskSearch, setRiskSearch] = useState('');
  const [riskCategoryFilter, setRiskCategoryFilter] = useState('all');
  const [riskStatusFilter, setRiskStatusFilter] = useState('all');
  const [issueSearch, setIssueSearch] = useState('');
  const [issueCategoryFilter, setIssueCategoryFilter] = useState('all');
  const [issueStatusFilter, setIssueStatusFilter] = useState('all');

  /* ── Data state ─────────────────────────────────────────────── */
  const [flags, setFlags] = useState<IssueFlagResponse[]>([]);
  const [risks, setRisks] = useState<ProjectRiskResponse[]>([]);
  const [issues, setIssues] = useState<ProjectIssueResponse[]>([]);
  const [userMap, setUserMap] = useState<Map<string, SecurityUserResponse>>(
    new Map()
  );
  const [taskMap, setTaskMap] = useState<Map<string, GanttTaskResponse>>(
    new Map()
  );
  const [roleSlots, setRoleSlots] = useState<ProjectRoleSlotResponse[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  /* ── Drawer state ───────────────────────────────────────────── */
  const [selectedRisk, setSelectedRisk] =
    useState<ProjectRiskResponse | null>(null);
  const [selectedIssue, setSelectedIssue] =
    useState<ProjectIssueResponse | null>(null);
  const [drawerEditing, setDrawerEditing] = useState(false);
  const [riskDraft, setRiskDraft] = useState<Partial<ProjectRiskResponse>>({});
  const [issueDraft, setIssueDraft] = useState<
    Partial<ProjectIssueResponse>
  >({});
  const [drawerBusy, setDrawerBusy] = useState(false);

  /* ── Create-new state ───────────────────────────────────────── */
  const [showNewRisk, setShowNewRisk] = useState(false);
  const [showNewIssue, setShowNewIssue] = useState(false);
  // DEV-832: HSE incident modal state — opens the ReportIncidentModal
  // with no initialCategory so the user picks Health/Safety/Environment.
  const [showHseIncidentModal, setShowHseIncidentModal] = useState(false);
  const [newRiskTitle, setNewRiskTitle] = useState('');
  const [newRiskLikelihood, setNewRiskLikelihood] = useState(3);
  const [newRiskImpact, setNewRiskImpact] = useState(3);
  const [newIssueTitle, setNewIssueTitle] = useState('');
  const [newIssueSeverity, setNewIssueSeverity] = useState('warning');

  /* ── Load ───────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!projectGuid) return;
    setLoading(true);
    setError(null);
    try {
      const [flagData, riskData, issueData, users, tasks, slots] = await Promise.all([
        fetchIssueFlags(projectGuid),
        fetchProjectRisks(projectGuid).catch((e) => {
          console.warn('[RisksIssuesTab] risks load failed:', e);
          return [] as ProjectRiskResponse[];
        }),
        fetchProjectIssues(projectGuid).catch((e) => {
          console.warn('[RisksIssuesTab] issues load failed:', e);
          return [] as ProjectIssueResponse[];
        }),
        fetchAllSecurityUsers().catch((e) => {
          console.warn('[RisksIssuesTab] user lookup failed:', e);
          return [] as SecurityUserResponse[];
        }),
        fetchGanttTasksByProject(projectGuid).catch((e) => {
          console.warn('[RisksIssuesTab] gantt lookup failed:', e);
          return [] as GanttTaskResponse[];
        }),
        fetchProjectRoleSlots(projectGuid).catch((e) => {
          console.warn('[RisksIssuesTab] role slots load failed:', e);
          return [] as ProjectRoleSlotResponse[];
        }),
      ]);

      setFlags(flagData);
      setRisks(riskData);
      setIssues(issueData);
      setRoleSlots(slots.filter((s) => s.status !== 'closed'));

      const um = new Map<string, SecurityUserResponse>();
      users.forEach((u) => um.set(guidKey(u.userGuid), u));
      setUserMap(um);

      const tm = new Map<string, GanttTaskResponse>();
      tasks.forEach((t) => tm.set(guidKey(t.id), t));
      setTaskMap(tm);

      if (debugMode) {
        console.info(
          `[RisksIssuesTab] Loaded ${flagData.length} flag(s), ` +
            `${riskData.length} risk(s), ${issueData.length} issue(s)`
        );
      }
    } catch (e: any) {
      console.error('[RisksIssuesTab] load failed:', e);
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [projectGuid, debugMode]);

  useEffect(() => {
    load();
  }, [load]);

  /* ── Flag mutations ─────────────────────────────────────────── */
  const mutateFlag = useCallback(
    async (
      flag: IssueFlagResponse,
      targetStatus: 'resolved' | 'dismissed' | 'open'
    ) => {
      const promptKey =
        targetStatus === 'resolved'
          ? 'projectDetail.risksIssues.notePromptResolve'
          : targetStatus === 'dismissed'
          ? 'projectDetail.risksIssues.notePromptDismiss'
          : 'projectDetail.risksIssues.notePromptReopen';
      const raw =
        typeof window !== 'undefined' ? window.prompt(t(promptKey)) : null;
      if (raw === null) return;
      const note = raw.trim() || undefined;

      setBusyIds((prev) => {
        const next = new Set(prev);
        next.add(flag.id);
        return next;
      });
      try {
        const updated = await resolveIssueFlag(projectGuid, flag.id, {
          status: targetStatus,
          resolutionNote: note,
        });
        setFlags((prev) =>
          prev.map((f) => (f.id === flag.id ? updated : f))
        );
        onFlagsChanged?.();
      } catch (e: any) {
        console.error(
          `[RisksIssuesTab] ${targetStatus} failed for ${flag.id}:`,
          e
        );
        setError(e?.message || `Failed to ${targetStatus} flag`);
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(flag.id);
          return next;
        });
      }
    },
    [projectGuid, t, onFlagsChanged]
  );

  const toggleExpand = useCallback((flagId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(flagId)) next.delete(flagId);
      else next.add(flagId);
      return next;
    });
  }, []);

  /* ── Risk mutations ─────────────────────────────────────────── */
  const saveRisk = useCallback(async () => {
    if (!selectedRisk || Object.keys(riskDraft).length === 0) return;
    setDrawerBusy(true);
    try {
      const updated = await updateProjectRisk(
        projectGuid,
        selectedRisk.id,
        riskDraft as any
      );
      setRisks((prev) =>
        prev.map((r) => (r.id === selectedRisk.id ? updated : r))
      );
      setSelectedRisk(updated);
      setDrawerEditing(false);
      setRiskDraft({});
    } catch (e: any) {
      setError(e?.message || 'Failed to save risk');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedRisk, riskDraft]);

  const handleDeleteRisk = useCallback(async () => {
    if (!selectedRisk) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('projectDetail.risksIssues.confirmDelete'))
    )
      return;
    setDrawerBusy(true);
    try {
      await deleteProjectRisk(projectGuid, selectedRisk.id);
      setRisks((prev) => prev.filter((r) => r.id !== selectedRisk.id));
      setSelectedRisk(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete risk');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedRisk, t]);

  const handleMaterialize = useCallback(async () => {
    if (!selectedRisk) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('projectDetail.risksIssues.confirmMaterialize'))
    )
      return;
    setDrawerBusy(true);
    try {
      const result = await materializeRisk(projectGuid, selectedRisk.id);
      const updatedRisk = result.risk;
      setRisks((prev) =>
        prev.map((r) => (r.id === selectedRisk.id ? updatedRisk : r))
      );
      setSelectedRisk(updatedRisk);
      // Re-fetch issues since a new one was created server-side
      const freshIssues = await fetchProjectIssues(projectGuid);
      setIssues(freshIssues);
    } catch (e: any) {
      setError(e?.message || 'Failed to materialize risk');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedRisk, t]);

  const handleCreateRisk = useCallback(async () => {
    if (!newRiskTitle.trim()) return;
    setDrawerBusy(true);
    try {
      const created = await createProjectRisk(projectGuid, {
        title: newRiskTitle.trim(),
        likelihood: newRiskLikelihood,
        impact: newRiskImpact,
      });
      setRisks((prev) => [created, ...prev]);
      setShowNewRisk(false);
      setNewRiskTitle('');
      setNewRiskLikelihood(3);
      setNewRiskImpact(3);
    } catch (e: any) {
      setError(e?.message || 'Failed to create risk');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, newRiskTitle, newRiskLikelihood, newRiskImpact]);

  /* ── Issue mutations ────────────────────────────────────────── */
  const saveIssue = useCallback(async () => {
    if (!selectedIssue || Object.keys(issueDraft).length === 0) return;
    setDrawerBusy(true);
    // DEV-822 spillover diagnostic: confirm what the PATCH actually sends.
    // Remove once the persistence bug is closed.
    console.info(
      '[issues:saveIssue] PATCH payload',
      JSON.stringify(issueDraft, null, 2),
    );
    try {
      const updated = await updateProjectIssue(
        projectGuid,
        selectedIssue.id,
        issueDraft as any
      );
      console.info(
        '[issues:saveIssue] response description=',
        JSON.stringify(updated.description),
        ' resolutionPlan=',
        JSON.stringify(updated.resolutionPlan),
      );
      setIssues((prev) =>
        prev.map((i) => (i.id === selectedIssue.id ? updated : i))
      );
      setSelectedIssue(updated);
      setDrawerEditing(false);
      setIssueDraft({});
    } catch (e: any) {
      setError(e?.message || 'Failed to save issue');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedIssue, issueDraft]);

  const handleDeleteIssue = useCallback(async () => {
    if (!selectedIssue) return;
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('projectDetail.risksIssues.confirmDelete'))
    )
      return;
    setDrawerBusy(true);
    try {
      await deleteProjectIssue(projectGuid, selectedIssue.id);
      setIssues((prev) => prev.filter((i) => i.id !== selectedIssue.id));
      setSelectedIssue(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete issue');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, selectedIssue, t]);

  const handleCreateIssue = useCallback(async () => {
    if (!newIssueTitle.trim()) return;
    setDrawerBusy(true);
    try {
      const created = await createProjectIssue(projectGuid, {
        title: newIssueTitle.trim(),
        severity: newIssueSeverity,
      });
      setIssues((prev) => [created, ...prev]);
      setShowNewIssue(false);
      setNewIssueTitle('');
      setNewIssueSeverity('warning');
    } catch (e: any) {
      setError(e?.message || 'Failed to create issue');
    } finally {
      setDrawerBusy(false);
    }
  }, [projectGuid, newIssueTitle, newIssueSeverity]);

  /* ── Filtered lists ─────────────────────────────────────────── */
  const flagCounts = useMemo(() => {
    const c = { open: 0, resolved: 0, dismissed: 0, all: flags.length };
    for (const f of flags) {
      if (f.status === 'open') c.open++;
      if (f.status === 'resolved') c.resolved++;
      if (f.status === 'dismissed') c.dismissed++;
    }
    return c;
  }, [flags]);

  const visibleFlags = useMemo(() => {
    if (flagFilter === 'all') return flags;
    return flags.filter((f) => f.status === flagFilter);
  }, [flags, flagFilter]);

  const visibleRisks = useMemo(() => {
    let list = risks;
    if (riskStatusFilter !== 'all') {
      list = list.filter((r) => r.status === riskStatusFilter);
    }
    if (riskCategoryFilter !== 'all') {
      list = list.filter((r) => r.category === riskCategoryFilter);
    }
    if (riskSearch.trim()) {
      const q = riskSearch.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          (r.description ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [risks, riskStatusFilter, riskCategoryFilter, riskSearch]);

  const visibleIssues = useMemo(() => {
    let list = issues;
    if (issueStatusFilter !== 'all') {
      list = list.filter((i) => i.status === issueStatusFilter);
    }
    if (issueCategoryFilter !== 'all') {
      list = list.filter((i) => i.category === issueCategoryFilter);
    }
    if (issueSearch.trim()) {
      const q = issueSearch.toLowerCase();
      list = list.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          (i.description ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [issues, issueStatusFilter, issueCategoryFilter, issueSearch]);

  /* ── Name resolution ────────────────────────────────────────── */
  const resolveUser = useCallback(
    (guid: string | null | undefined): string => {
      if (!guid) return t('projectDetail.risksIssues.unknownUser');
      const u = userMap.get(guidKey(guid));
      return (
        u?.displayName ||
        u?.name ||
        t('projectDetail.risksIssues.unknownUser')
      );
    },
    [userMap, t]
  );

  /** Resolve a role slot GUID to its label (e.g. "Project Manager"). */
  const resolveRoleSlot = useCallback(
    (slotGuid: string | null | undefined): string => {
      if (!slotGuid) return t('projectDetail.risksIssues.unassigned');
      const slot = roleSlots.find(
        (s) => s.roleSlotGuid.toUpperCase() === slotGuid.toUpperCase()
      );
      return slot?.roleLabel || t('projectDetail.risksIssues.unassigned');
    },
    [roleSlots, t]
  );

  const resolveTask = useCallback(
    (taskId: string | null | undefined): GanttTaskResponse | null => {
      if (!taskId) return null;
      return taskMap.get(guidKey(taskId)) || null;
    },
    [taskMap]
  );

  /* ── Risk / Issue counts for tab badges ─────────────────────── */
  const openRiskCount = useMemo(
    () => risks.filter((r) => r.status !== 'closed').length,
    [risks]
  );
  const openIssueCount = useMemo(
    () => issues.filter((i) => i.status !== 'closed' && i.status !== 'resolved').length,
    [issues]
  );

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className={styles.root}>
      {/* Outer view tabs */}
      <div className={styles.viewTabs} role="tablist">
        <button
          role="tab"
          aria-selected={view === 'flags'}
          className={view === 'flags' ? styles.viewTabActive : styles.viewTab}
          onClick={() => setView('flags')}
        >
          {t('projectDetail.risksIssues.flagInbox')}
          {flagCounts.open > 0 && (
            <span
              className={
                view === 'flags' ? styles.badgeActive : styles.badge
              }
            >
              {flagCounts.open}
            </span>
          )}
        </button>
        <button
          role="tab"
          aria-selected={view === 'risks'}
          className={view === 'risks' ? styles.viewTabActive : styles.viewTab}
          onClick={() => setView('risks')}
        >
          {t('projectDetail.risksIssues.riskRegister')}
          {openRiskCount > 0 && (
            <span
              className={
                view === 'risks' ? styles.badgeActive : styles.badge
              }
            >
              {openRiskCount}
            </span>
          )}
        </button>
        <button
          role="tab"
          aria-selected={view === 'issues'}
          className={
            view === 'issues' ? styles.viewTabActive : styles.viewTab
          }
          onClick={() => setView('issues')}
        >
          {t('projectDetail.risksIssues.issuesRegister')}
          {openIssueCount > 0 && (
            <span
              className={
                view === 'issues' ? styles.badgeActive : styles.badge
              }
            >
              {openIssueCount}
            </span>
          )}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* ── Flag Inbox ───────────────────────────────────────────── */}
      {view === 'flags' && (
        <>
          <div
            className={styles.filterRow}
            role="tablist"
            aria-label="Flag status filter"
          >
            {(
              ['open', 'resolved', 'dismissed', 'all'] as FlagFilterKey[]
            ).map((key) => {
              const active = flagFilter === key;
              const labelKey =
                key === 'open'
                  ? 'projectDetail.risksIssues.filterOpen'
                  : key === 'resolved'
                  ? 'projectDetail.risksIssues.filterResolved'
                  : key === 'dismissed'
                  ? 'projectDetail.risksIssues.filterDismissed'
                  : 'projectDetail.risksIssues.filterAll';
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={active}
                  className={
                    active ? styles.filterChipActive : styles.filterChip
                  }
                  onClick={() => setFlagFilter(key)}
                >
                  {t(labelKey)}
                  <span className={styles.filterCount}>
                    {flagCounts[key]}
                  </span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className={styles.loading}>…</div>
          ) : visibleFlags.length === 0 ? (
            <div className={styles.emptyState}>
              {flagFilter === 'open'
                ? t('projectDetail.risksIssues.noFlags')
                : t('projectDetail.risksIssues.noFlagsFiltered')}
            </div>
          ) : (
            <div className={styles.flagList}>
              {visibleFlags.map((flag) => {
                const busy = busyIds.has(flag.id);
                const expanded = expandedIds.has(flag.id);
                const task = resolveTask(flag.ganttTaskId);
                const reporterName = resolveUser(flag.reportedByUserGuid);
                const assigneeName = task?.assignedTo
                  ? resolveUser(task.assignedTo)
                  : null;
                const resolverName = flag.resolvedBy
                  ? resolveUser(flag.resolvedBy)
                  : null;

                return (
                  <div key={flag.id} className={rowClass(flag)}>
                    <div className={styles.flagHeader}>
                      <div className={styles.flagBody}>
                        <div className={styles.flagDesc}>
                          {flag.description}
                        </div>
                        <div className={styles.flagMeta}>
                          <span
                            className={`${styles.severityPill} ${severityClass(flag.severity)}`}
                          >
                            {flag.severity}
                          </span>
                          {flag.status !== 'open' && (
                            <span
                              className={`${styles.statusPill} ${statusClass(flag.status)}`}
                            >
                              {flag.status}
                            </span>
                          )}
                          {flag.promotedToType && (
                            <span className={styles.promotedPill}>
                              {t('projectDetail.risksIssues.promoted')} →{' '}
                              {flag.promotedToType}
                            </span>
                          )}
                          {task ? (
                            <span
                              className={styles.taskLink}
                              title={task.taskName}
                            >
                              {task.taskName}
                            </span>
                          ) : (
                            <span className={styles.projectLevelPill}>
                              {t('projectDetail.risksIssues.projectLevel')}
                            </span>
                          )}
                          <span>
                            {t('projectDetail.risksIssues.reporter')}:{' '}
                            {reporterName}
                          </span>
                          <span>{formatDate(flag.createdAt)}</span>
                          <button
                            type="button"
                            className={styles.expandBtn}
                            onClick={() => toggleExpand(flag.id)}
                            aria-expanded={expanded}
                          >
                            {expanded
                              ? t('projectDetail.risksIssues.hideDetails')
                              : t('projectDetail.risksIssues.showDetails')}
                          </button>
                        </div>
                      </div>
                      {canEdit && (
                        <div className={styles.flagActions}>
                          {flag.status === 'open' ? (
                            <>
                              <button
                                className={styles.resolveBtn}
                                disabled={busy}
                                onClick={() => mutateFlag(flag, 'resolved')}
                              >
                                {t('projectDetail.risksIssues.resolve')}
                              </button>
                              <button
                                className={styles.dismissBtn}
                                disabled={busy}
                                onClick={() => mutateFlag(flag, 'dismissed')}
                              >
                                {t('projectDetail.risksIssues.dismiss')}
                              </button>
                            </>
                          ) : (
                            <button
                              className={styles.reopenBtn}
                              disabled={busy}
                              onClick={() => mutateFlag(flag, 'open')}
                            >
                              {t('projectDetail.risksIssues.reopen')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {expanded && (
                      <div className={styles.details}>
                        {task && (
                          <>
                            <div className={styles.detailLabel}>
                              {t('projectDetail.risksIssues.assignee')}
                            </div>
                            <div className={styles.detailValue}>
                              {assigneeName ?? '—'}
                            </div>
                            <div className={styles.detailLabel}>
                              {t('projectDetail.risksIssues.dueDate')}
                            </div>
                            <div className={styles.detailValue}>
                              {formatDate(task.startDate)} →{' '}
                              {formatDate(task.endDate)}
                            </div>
                            <div className={styles.detailLabel}>
                              {t('projectDetail.risksIssues.percentComplete')}
                            </div>
                            <div className={styles.detailValue}>
                              {Math.round(task.percentComplete ?? 0)}%
                            </div>
                          </>
                        )}
                        {flag.status === 'resolved' ||
                        flag.status === 'dismissed' ? (
                          <>
                            <div className={styles.detailLabel}>
                              {flag.status === 'dismissed'
                                ? t(
                                    'projectDetail.risksIssues.dismissedBy'
                                  )
                                : t(
                                    'projectDetail.risksIssues.resolvedBy'
                                  )}
                            </div>
                            <div className={styles.detailValue}>
                              {resolverName ?? '—'}
                              {flag.resolvedAt
                                ? ` · ${formatDate(flag.resolvedAt)}`
                                : ''}
                            </div>
                          </>
                        ) : null}
                        {flag.resolutionNote && (
                          <>
                            <div className={styles.detailLabel}>
                              {t(
                                'projectDetail.risksIssues.resolutionNote'
                              )}
                            </div>
                            <div className={styles.detailNote}>
                              {flag.resolutionNote}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Risk Register ────────────────────────────────────────── */}
      {view === 'risks' && (
        <div className={styles.registerContainer}>
          {/* Horizontal filter bar */}
          <div className={styles.registerFilterBar}>
            <select
              className={styles.registerFilter}
              value={riskStatusFilter}
              onChange={(e) => setRiskStatusFilter(e.target.value)}
            >
              <option value="all">
                {t('projectDetail.risksIssues.allStatuses')}
              </option>
              {RISK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`projectDetail.risksIssues.riskStatus_${s}`)}
                </option>
              ))}
            </select>
            <select
              className={styles.registerFilter}
              value={riskCategoryFilter}
              onChange={(e) => setRiskCategoryFilter(e.target.value)}
            >
              <option value="all">
                {t('projectDetail.risksIssues.allCategories')}
              </option>
              {RISK_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`projectDetail.risksIssues.category_${c}`)}
                </option>
              ))}
            </select>
            <input
              type="text"
              className={styles.registerSearch}
              placeholder={t('projectDetail.risksIssues.searchPlaceholder')}
              value={riskSearch}
              onChange={(e) => setRiskSearch(e.target.value)}
            />
            {/* View toggle: list / heatmap */}
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={riskView === 'list' ? styles.viewToggleBtnActive : styles.viewToggleBtn}
                onClick={() => setRiskView('list')}
                title={t('projectDetail.risksIssues.listView')}
              >
                ☰
              </button>
              <button
                type="button"
                className={riskView === 'heatmap' ? styles.viewToggleBtnActive : styles.viewToggleBtn}
                onClick={() => setRiskView('heatmap')}
                title={t('projectDetail.risksIssues.heatmapView')}
              >
                ▦
              </button>
            </div>

            {canEdit && (
              <button
                className={styles.addBtn}
                onClick={() => setShowNewRisk(true)}
              >
                + {t('projectDetail.risksIssues.newRisk')}
              </button>
            )}
          </div>

          {/* Inline create form */}
          {showNewRisk && (
            <div className={styles.inlineCreate}>
              <input
                className={styles.inlineCreateInput}
                placeholder={t(
                  'projectDetail.risksIssues.riskTitlePlaceholder'
                )}
                value={newRiskTitle}
                onChange={(e) => setNewRiskTitle(e.target.value)}
                autoFocus
              />
              <div className={styles.inlineCreateRow}>
                <AssessmentPanel
                  likelihood={newRiskLikelihood}
                  impact={newRiskImpact}
                  editing={true}
                  onLikelihoodChange={setNewRiskLikelihood}
                  onImpactChange={setNewRiskImpact}
                  t={t}
                />
              </div>
              <div className={styles.inlineCreateActions}>
                <button
                  className={styles.drawerSaveBtn}
                  onClick={handleCreateRisk}
                  disabled={drawerBusy || !newRiskTitle.trim()}
                >
                  {t('projectDetail.risksIssues.create')}
                </button>
                <button
                  className={styles.drawerCancelBtn}
                  onClick={() => {
                    setShowNewRisk(false);
                    setNewRiskTitle('');
                  }}
                >
                  {t('projectDetail.risksIssues.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Risk list or heatmap */}
          {loading ? (
            <div className={styles.loading}>…</div>
          ) : riskView === 'heatmap' ? (
            <RiskHeatmap
              risks={visibleRisks}
              onSelectRisk={(r) => {
                setSelectedRisk(r);
                setDrawerEditing(false);
                setRiskDraft({});
              }}
              t={t}
            />
          ) : visibleRisks.length === 0 ? (
            <div className={styles.emptyState}>
              {t('projectDetail.risksIssues.noRisks')}
            </div>
          ) : (
            <div className={styles.registerList}>
              {/* Header row */}
              <div className={styles.registerHeaderRow}>
                <span className={styles.registerColTitle}>
                  {t('projectDetail.risksIssues.riskTitle')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.category')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.riskScore')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.status')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.owner')}
                </span>
              </div>

              {visibleRisks.map((risk) => {
                const score = risk.likelihood * risk.impact;
                return (
                  <div
                    key={risk.id}
                    className={`${styles.registerRow} ${
                      risk.isKeyRisk ? styles.registerRowKey : ''
                    }`}
                    onClick={() => {
                      setSelectedRisk(risk);
                      setDrawerEditing(false);
                      setRiskDraft({});
                    }}
                  >
                    <span className={styles.registerColTitle}>
                      {risk.isKeyRisk && (
                        <span className={styles.keyIndicator}>★</span>
                      )}
                      {risk.title}
                    </span>
                    <span className={styles.registerColSmall}>
                      {t(
                        `projectDetail.risksIssues.category_${risk.category}`
                      )}
                    </span>
                    <span className={styles.registerColSmall}>
                      <RiskScoreBar score={score} compact />
                    </span>
                    <span className={styles.registerColSmall}>
                      {t(
                        `projectDetail.risksIssues.riskStatus_${risk.status}`
                      )}
                    </span>
                    <span className={styles.registerColSmall}>
                      {risk.ownerRoleSlotGuid
                        ? resolveRoleSlot(risk.ownerRoleSlotGuid)
                        : resolveUser(risk.ownerUserGuid)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Risk Drawer overlay — portalled to body to escape stacking context */}
          {selectedRisk && createPortal(
            <RiskDrawer
              risk={selectedRisk}
              editing={drawerEditing}
              setEditing={setDrawerEditing}
              draft={riskDraft}
              setDraft={setRiskDraft}
              onSave={saveRisk}
              onClose={() => {
                setSelectedRisk(null);
                setDrawerEditing(false);
                setRiskDraft({});
              }}
              onDelete={handleDeleteRisk}
              onMaterialize={handleMaterialize}
              canEdit={canEdit}
              busy={drawerBusy}
              resolveUser={resolveUser}
              resolveRoleSlot={resolveRoleSlot}
              roleSlots={roleSlots}
              t={t}
            />,
            document.body,
          )}
        </div>
      )}

      {/* ── Issues Register ──────────────────────────────────────── */}
      {view === 'issues' && (
        <div className={styles.registerContainer}>
          {/* Horizontal filter bar */}
          <div className={styles.registerFilterBar}>
            <select
              className={styles.registerFilter}
              value={issueStatusFilter}
              onChange={(e) => setIssueStatusFilter(e.target.value)}
            >
              <option value="all">
                {t('projectDetail.risksIssues.allStatuses')}
              </option>
              {ISSUE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`projectDetail.risksIssues.issueStatus_${s}`)}
                </option>
              ))}
            </select>
            <select
              className={styles.registerFilter}
              value={issueCategoryFilter}
              onChange={(e) => setIssueCategoryFilter(e.target.value)}
            >
              <option value="all">
                {t('projectDetail.risksIssues.allCategories')}
              </option>
              {ISSUE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`projectDetail.risksIssues.category_${c}`)}
                </option>
              ))}
            </select>
            <input
              type="text"
              className={styles.registerSearch}
              placeholder={t('projectDetail.risksIssues.searchPlaceholder')}
              value={issueSearch}
              onChange={(e) => setIssueSearch(e.target.value)}
            />
            {canEdit && (
              <>
                <button
                  className={styles.addBtn}
                  onClick={() => setShowNewIssue(true)}
                >
                  + {t('projectDetail.risksIssues.newIssue')}
                </button>
                {/* DEV-832: HSE incident entry from the Issues tab.
                    Opens the same ReportIncidentModal the per-card +Report
                    button uses, but with category unlocked so the user
                    picks Health/Safety/Environment in the modal. */}
                <button
                  className={styles.addBtn}
                  onClick={() => setShowHseIncidentModal(true)}
                  title={t(
                    'projectDetail.risksIssues.newHseIncidentHint',
                    'Log a Health, Safety, or Environmental incident or near-miss',
                  )}
                  style={{ marginInlineStart: 8 }}
                >
                  + {t('projectDetail.risksIssues.newHseIncident', 'HSE incident')}
                </button>
              </>
            )}
          </div>

          {/* Inline create form */}
          {showNewIssue && (
            <div className={styles.inlineCreate}>
              <input
                className={styles.inlineCreateInput}
                placeholder={t(
                  'projectDetail.risksIssues.issueTitlePlaceholder'
                )}
                value={newIssueTitle}
                onChange={(e) => setNewIssueTitle(e.target.value)}
                autoFocus
              />
              <div className={styles.inlineCreateRow}>
                <label className={styles.drawerFieldLabel}>
                  {t('projectDetail.risksIssues.severity')}
                </label>
                <select
                  className={styles.registerFilter}
                  value={newIssueSeverity}
                  onChange={(e) => setNewIssueSeverity(e.target.value)}
                >
                  {ISSUE_SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {t(`projectDetail.risksIssues.severity_${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.inlineCreateActions}>
                <button
                  className={styles.drawerSaveBtn}
                  onClick={handleCreateIssue}
                  disabled={drawerBusy || !newIssueTitle.trim()}
                >
                  {t('projectDetail.risksIssues.create')}
                </button>
                <button
                  className={styles.drawerCancelBtn}
                  onClick={() => {
                    setShowNewIssue(false);
                    setNewIssueTitle('');
                  }}
                >
                  {t('projectDetail.risksIssues.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Issue list */}
          {loading ? (
            <div className={styles.loading}>…</div>
          ) : visibleIssues.length === 0 ? (
            <div className={styles.emptyState}>
              {t('projectDetail.risksIssues.noIssues')}
            </div>
          ) : (
            <div className={styles.registerList}>
              <div className={styles.registerHeaderRow}>
                <span className={styles.registerColTitle}>
                  {t('projectDetail.risksIssues.issueTitle')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.severity')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.category')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.status')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.dueDate')}
                </span>
                <span className={styles.registerColSmall}>
                  {t('projectDetail.risksIssues.owner')}
                </span>
              </div>

              {visibleIssues.map((issue) => (
                <div
                  key={issue.id}
                  className={`${styles.registerRow} ${
                    issue.isKeyIssue ? styles.registerRowKey : ''
                  }`}
                  onClick={() => {
                    setSelectedIssue(issue);
                    setDrawerEditing(false);
                    setIssueDraft({});
                  }}
                >
                  <span className={styles.registerColTitle}>
                    {issue.isKeyIssue && (
                      <span className={styles.keyIndicator}>★</span>
                    )}
                    {issue.title}
                  </span>
                  <span className={styles.registerColSmall}>
                    <span
                      className={`${styles.severityPill} ${
                        issue.severity === 'blocking'
                          ? styles.severityBlocking
                          : issue.severity === 'warning'
                          ? styles.severityWarning
                          : styles.severityInfo
                      }`}
                    >
                      {t(
                        `projectDetail.risksIssues.severity_${issue.severity}`
                      )}
                    </span>
                  </span>
                  <span className={styles.registerColSmall}>
                    {t(
                      `projectDetail.risksIssues.category_${issue.category}`
                    )}
                  </span>
                  <span className={styles.registerColSmall}>
                    {t(
                      `projectDetail.risksIssues.issueStatus_${issue.status}`
                    )}
                  </span>
                  <span className={styles.registerColSmall}>
                    {formatDate(issue.dueDate)}
                  </span>
                  <span className={styles.registerColSmall}>
                    {issue.ownerRoleSlotGuid
                      ? resolveRoleSlot(issue.ownerRoleSlotGuid)
                      : resolveUser(issue.ownerUserGuid)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Issue Drawer overlay — portalled to body to escape stacking context */}
          {selectedIssue && createPortal(
            <IssueDrawer
              issue={selectedIssue}
              editing={drawerEditing}
              setEditing={setDrawerEditing}
              draft={issueDraft}
              setDraft={setIssueDraft}
              onSave={saveIssue}
              onClose={() => {
                setSelectedIssue(null);
                setDrawerEditing(false);
                setIssueDraft({});
              }}
              onDelete={handleDeleteIssue}
              canEdit={canEdit}
              busy={drawerBusy}
              resolveUser={resolveUser}
              resolveRoleSlot={resolveRoleSlot}
              roleSlots={roleSlots}
              t={t}
            />,
            document.body,
          )}
        </div>
      )}

      {/* DEV-832: HSE Incident entry from the Issues tab. Same modal as
          the per-card +Report path, but with category unlocked so the
          user picks which HSE category. After save, refresh the Issues
          list so the new HSE incident appears alongside standard issues. */}
      <ReportIncidentModal
        open={showHseIncidentModal}
        projectGuid={projectGuid}
        onClose={() => setShowHseIncidentModal(false)}
        onSaved={() => {
          // Modal already invalidates HSE incident cache. Trigger an Issues
          // list refresh too so the new HSE incident appears in the standard
          // Issues list immediately.
          setShowHseIncidentModal(false);
          // Re-fetch issues by toggling refresh trigger if available.
          // For now, page reload fallback — fix in DEV-837 hardening if needed.
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('l4e:project-issues-changed', {
              detail: { projectGuid },
            }));
          }
        }}
      />
    </div>
  );
}
